/**
 * Spent proof events for Cashero.
 *
 * When a wallet sends ecash to another wallet (P2P, bypassing the mint),
 * it publishes a spent-proof event (kind 5401) encrypted to the mint's npub.
 * This allows the mint to track which secrets have been spent, preventing
 * double-spending even when tokens change hands off-chain.
 *
 * The event content is NIP-44 encrypted to the mint's public key.
 * Only the mint can decrypt and read the spent secret hashes.
 */

import type { NostrEvent, EventTemplate } from 'nostr-tools';
import { finalizeEvent, getPublicKey } from 'nostr-tools';
import { hexToBytes } from '@noble/hashes/utils';

import { SPENT_PROOF_KIND } from './constants';
import { nip44Encrypt, nip44Decrypt } from './gift-wrap';

/** Payload of a spent proof (before encryption) */
export interface SpentProofPayload {
  /** SHA-256 hashes of the secrets that have been spent */
  secretHashes: string[];
  /** When the tokens were spent (unix timestamp) */
  spentAt: number;
  /** The mint's pubkey (hex) these proofs belong to */
  mintPubkey: string;
}

/**
 * Create a spent proof event.
 *
 * The content is NIP-44 encrypted so only the mint can read the secret hashes.
 *
 * @param secretHashes - SHA-256 hashes of the secrets being marked as spent
 * @param mintPubkeyHex - The mint's public key (hex)
 * @param walletPrivkeyHex - The wallet user's private key (hex)
 * @returns The signed spent proof event
 */
export function createSpentProofEvent(
  secretHashes: string[],
  mintPubkeyHex: string,
  walletPrivkeyHex: string,
): NostrEvent {
  const payload: SpentProofPayload = {
    secretHashes,
    spentAt: Math.floor(Date.now() / 1000),
    mintPubkey: mintPubkeyHex,
  };

  const encrypted = nip44Encrypt(payload, walletPrivkeyHex, mintPubkeyHex);

  const walletPrivkeyBytes = hexToBytes(walletPrivkeyHex);
  const pubkey = getPublicKey(walletPrivkeyBytes);

  const template: EventTemplate = {
    kind: SPENT_PROOF_KIND,
    content: encrypted,
    tags: [
      ['p', mintPubkeyHex],
    ],
    created_at: Math.floor(Date.now() / 1000) - 1,
    pubkey,
  };

  return finalizeEvent(template, walletPrivkeyBytes) as NostrEvent;
}

/**
 * Parse a spent proof event (decrypts content using the mint's private key).
 *
 * @param event - The kind 5401 Nostr event
 * @param mintPrivkeyHex - The mint's private key (hex) to decrypt
 * @returns The decrypted spent proof payload, or null if decryption fails
 */
export function parseSpentProofEvent(
  event: NostrEvent,
  mintPrivkeyHex: string,
): SpentProofPayload | null {
  if (event.kind !== SPENT_PROOF_KIND) return null;

  try {
    return nip44Decrypt<SpentProofPayload>(event.content, mintPrivkeyHex, event.pubkey);
  } catch {
    return null;
  }
}

/**
 * Build a Nostr filter to get spent proof events intended for a mint.
 */
export function getSpentProofFilter(mintPubkey: string) {
  return {
    kinds: [SPENT_PROOF_KIND],
    '#p': [mintPubkey],
    limit: 100,
  };
}
